import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenderItemsApi, CreateTenderItemDto, UpdateTenderItemDto } from '../../../api/tenderItems';
import { notifications } from '@mantine/notifications';

export function useTenderItems(tenderId: string, useVendorEndpoint: boolean = false) {
  return useQuery({
    queryKey: ['tenderItems', tenderId, useVendorEndpoint ? 'vendor' : 'admin'],
    queryFn: () => useVendorEndpoint 
      ? tenderItemsApi.listVendorTenderItems(tenderId)
      : tenderItemsApi.listTenderItems(tenderId),
    enabled: !!tenderId,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on component mount if data exists
  });
}

export function useTenderItem(tenderId: string, itemId: string) {
  return useQuery({
    queryKey: ['tenderItem', tenderId, itemId],
    queryFn: () => tenderItemsApi.getTenderItemById(tenderId, itemId),
    enabled: !!tenderId && !!itemId,
  });
}

export function useCreateTenderItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, data }: { tenderId: string; data: CreateTenderItemDto }) =>
      tenderItemsApi.createTenderItem(tenderId, data),
    onSuccess: (_, { tenderId }) => {
      queryClient.invalidateQueries({ queryKey: ['tenderItems', tenderId] });
      notifications.show({
        title: 'Success',
        message: 'Tender item created successfully',
        color: 'green',
      });
    },
    onError: (error) => {
      console.error('Error creating tender item:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to create tender item',
        color: 'red',
      });
    },
  });
}

export function useUpdateTenderItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, itemId, data }: { tenderId: string; itemId: string; data: UpdateTenderItemDto }) =>
      tenderItemsApi.updateTenderItem(tenderId, itemId, data),
    onSuccess: (_, { tenderId }) => {
      queryClient.invalidateQueries({ queryKey: ['tenderItems', tenderId] });
      notifications.show({
        title: 'Success',
        message: 'Tender item updated successfully',
        color: 'green',
      });
    },
    onError: (error) => {
      console.error('Error updating tender item:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update tender item',
        color: 'red',
      });
    },
  });
}

export function useRemoveTenderItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, itemId }: { tenderId: string; itemId: string }) =>
      tenderItemsApi.removeTenderItem(tenderId, itemId),
    onSuccess: (_, { tenderId }) => {
      queryClient.invalidateQueries({ queryKey: ['tenderItems', tenderId] });
      notifications.show({
        title: 'Success',
        message: 'Tender item removed successfully',
        color: 'green',
      });
    },
    onError: (error) => {
      console.error('Error removing tender item:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to remove tender item',
        color: 'red',
      });
    },
  });
}
